/**
 * "The session is gone" — announced, not acted on.
 *
 * A 401 can surface from any of the several requests the shell fires on mount (tenant,
 * permissions, notifications…). Each of those used to respond by navigating to the DCC
 * login itself, which meant a signed-out visit tore the page down mid-render: the app
 * painted, the Not Authorized screen appeared for an instant, and then a full-page
 * navigation blanked it while the browser waited on the other origin. Several handlers
 * racing to leave also made the sequence non-deterministic.
 *
 * So the request layer now only reports the fact, and one listener — the app-level gate in
 * components/Layout/main.tsx — decides what to show. Rendering beats navigating here: it
 * is instant, it cannot half-happen, and it keeps the user on a page that explains itself
 * with a sign-in button, rather than on whatever a slow cross-origin load looks like.
 */
export const SESSION_EXPIRED_EVENT = 'comply:session-expired';

/** Report that the backend has rejected our session. Safe to call repeatedly. */
export function emitSessionExpired(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

/** Subscribe to session loss. Returns an unsubscribe function. */
export function onSessionExpired(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(SESSION_EXPIRED_EVENT, handler);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
}
